/**
 * 자리판(교사 화면 + 인쇄) 공통 시각 비율.
 * 1이면 설계 기준 픽셀(78/72) 그대로. 1 미만이면 축소(한 화면에 더 많이 들어감).
 */
export const SEAT_BOARD_SCALE = 1;

export const SEAT_CELL_PX = Math.round(78 * SEAT_BOARD_SCALE);
export const SEAT_CHIP_PX = Math.round(72 * SEAT_BOARD_SCALE);
export const SEAT_CHIP_BORDER_RADIUS_PX = Math.round(36 * SEAT_BOARD_SCALE);

/** 드래그 시 칩이 마우스 아래로 내려오는 보정(px) */
export const SEAT_DRAG_TRANSLATE_Y_PX = Math.round(128 * SEAT_BOARD_SCALE);

export const seatBoardPx = (base: number): number => Math.round(base * SEAT_BOARD_SCALE);

/**
 * 뷰포트 너비에 비례한 교사 자리판 추가 배율.
 * 세로는 스크롤·드래그로 조정 가능하므로 높이는 반영하지 않는다.
 * `SEAT_BOARD_SCALE`과 곱해 실제 픽셀을 만든다.
 */
export function getTeacherSeatViewportScale(
  viewportWidth: number,
  _viewportHeight: number
): number {
  const REF_WIDTH = 840;
  const MIN = 0.76;
  const MAX = 1.52;
  return Math.min(MAX, Math.max(MIN, viewportWidth / REF_WIDTH));
}

/** 교사 화면용: 최종 배율(뷰포트·레이아웃 반영) 기준 셀·칩·보조 px */
export function getTeacherSeatRenderSizes(totalScale: number) {
  const s = SEAT_BOARD_SCALE * totalScale;
  return {
    cellPx: Math.round(78 * s),
    chipPx: Math.round(72 * s),
    chipBorderRadiusPx: Math.round(36 * s),
    dragTranslateYPx: Math.round(128 * s),
    px: (base: number) => Math.round(base * s)
  };
}

/**
 * 칠판+그리드(내부 패딩·좌석 간격 포함) 추정 바운딩 — SeatManagement 레이아웃과 맞춤.
 */
function estimateTeacherSeatBoardContentSize(
  gridCols: number,
  gridRows: number,
  totalScale: number
): { contentWidth: number; contentHeight: number } {
  const s = SEAT_BOARD_SCALE * totalScale;
  const cell = Math.round(78 * s);
  // 실제 그리드는 셀 크기만 사용한다. 모둠 간격(4/12/20)은 행마다 달라 열을 밀어내므로 쓰지 않는다.
  const gridW = gridCols * cell;
  const gridH = gridRows * cell;
  const iconH = Math.round(38 * s);
  const iconW = Math.round(500 * s);
  // 라벨(21) + gap(6) + 아이콘 + 하단 여유(4) + 컨테이너 gap(8)
  const boardH = 21 + 6 + iconH + 4;
  const innerPadY = 24; // padding 12*2 (편집) / 8*2 에 여유
  const innerPadX = 32;
  const slack = 16;
  const contentWidth = innerPadX + slack + Math.max(gridW, Math.min(iconW, 2000));
  const contentHeight = innerPadY + slack + boardH + 8 + gridH;
  return { contentWidth, contentHeight };
}

/**
 * 뷰포트 선호 배율(`viewportComfortScale`)을 상한으로,
 * 주어진 영역에 칠판+전체 그리드가 들어가도록 한 최대 배율.
 * `fitHeight`가 true이면 세로까지 맞춤(한 화면 표시). false면 너비만 맞추고 세로는 스크롤.
 */
export function getTeacherSeatCombinedScale(args: {
  availWidth: number;
  availHeight: number;
  gridCols: number;
  gridRows: number;
  viewportComfortScale: number;
  fitHeight?: boolean;
}): number {
  const margin = 8;
  const aw = Math.max(64, args.availWidth - margin);
  const ah = Math.max(64, args.availHeight - margin);
  const fitHeight = args.fitHeight === true;
  const cap = Math.max(0.48, Math.min(args.viewportComfortScale, 1.85));
  // 세로까지 맞출 때는 아주 작은 화면에서도 최대한 들어가도록 하한 완화
  const MIN = fitHeight ? 0.28 : 0.4;
  const { gridCols, gridRows } = args;

  const fits = (t: number) => {
    const { contentWidth, contentHeight } = estimateTeacherSeatBoardContentSize(
      gridCols,
      gridRows,
      t
    );
    if (contentWidth > aw) return false;
    if (fitHeight && contentHeight > ah) return false;
    return true;
  };

  if (fits(cap)) return cap;
  if (!fits(MIN)) return MIN;

  let low = MIN;
  let high = cap;
  for (let i = 0; i < 36; i++) {
    if (high - low < 0.0025) break;
    const mid = (low + high) / 2;
    if (fits(mid)) low = mid;
    else high = mid;
  }
  return low;
}

/**
 * 학생 좌석 예약 등 읽기 전용 자리판.
 * 교사 편집 화면보다 크게 두어 터치 타깃·가독성을 맞춤.
 */
export const SEAT_STUDENT_READ_SCALE = 1.22;

export const SEAT_STUDENT_CELL_PX = Math.round(78 * SEAT_STUDENT_READ_SCALE);
export const SEAT_STUDENT_CHIP_PX = Math.round(72 * SEAT_STUDENT_READ_SCALE);
export const SEAT_STUDENT_CHIP_BORDER_RADIUS_PX = Math.round(36 * SEAT_STUDENT_READ_SCALE);

export const studentSeatBoardPx = (base: number): number => Math.round(base * SEAT_STUDENT_READ_SCALE);
